/**
 * 商户风控中心 API（M4 PAGE-MCH-RISK-001~004，真实分支优先）
 * GET  /merchant/api/v1/risk/radar/metrics
 * GET  /merchant/api/v1/risk/radar/metrics/{metric}/drilldown
 * GET  /merchant/api/v1/risk/users
 * POST /merchant/api/v1/risk/users/{userId}/status
 * GET  /merchant/api/v1/risk/quick-actions
 * POST /merchant/api/v1/risk/quick-actions/batch-freeze|batch-limit|disputes
 * GET  /merchant/api/v1/risk/rules
 * PUT  /merchant/api/v1/risk/rules/{ruleId}/override
 */
import {
  merchantGet,
  merchantPost,
  merchantPut,
  USE_MOCK,
} from './client';

export { USE_MOCK };

// ================= DTO（对齐后端 application dto） =================

export type RiskRadarMetric = {
  metric: string;
  value: string;
  level: 'GREEN' | 'YELLOW' | 'RED';
  wow?: string | null;
  refreshedAt: string;
};

type RiskRadarDrilldown = {
  metric: string;
  items: Record<string, unknown>[];
};

export type RiskUser = {
  userId: string;
  userName: string;
  score: number;
  tags: string[];
  status: string;
};

type RiskUserListResult = {
  items: RiskUser[];
  total: number;
  page: number;
  size: number;
};

export type RiskQuickAction = {
  actionId: string;
  actionType: 'FREEZE' | 'LIMIT' | 'DISPUTE' | 'UNFREEZE_BATCH' | 'TRANSFER_BATCH' | string;
  targetCount: number;
  status: 'SUCCEED' | 'PARTIAL' | 'FAILED';
  operator: string;
  createdAt: string;
};

type RiskQuickActionListResult = {
  items: RiskQuickAction[];
  total: number;
};

export type RiskQuickActionSubmitResult = {
  actionId: string;
  actionType: string;
  targetCount: number;
  succeededCount: number;
  status: string;
  createdAt: string;
};

// ===== 快捷处置批量改版（S5-05，spec §3.2 冻结契约） =====

/** 候选卡视图（仅掩码；balance 为 BigDecimal 字符串） */
export type QuickActionCardView = {
  cardId: string | number;
  shortCode: string | null;
  panMasked: string;
  holderNameMasked: string;
  balance: string;
  currency: string;
  status: string;
};

type QuickActionCardPage = {
  total: number;
  items: QuickActionCardView[];
};

/** 行级失败明细（cards batch 与 quick-actions 共用结构） */
export type RiskBatchItemResult = {
  cardId: string | number;
  code: string;
  reason: string;
};

/** POST /quick-actions/batch-unfreeze 响应（非 FROZEN 跳过进明细） */
type QuickActionBatchUnfreezeResult = {
  processed: number;
  succeeded: (string | number)[];
  skipped: RiskBatchItemResult[];
  failed: RiskBatchItemResult[];
};


/** 候选卡筛选参数（任一或多条件模糊；spec §3.2 GET /cards） */
type QuickActionCardFilter = {
  cardholderName?: string;
  mobile?: string;
  cardNo?: string;
  cardId?: string;
  shortCode?: string;
  page?: number;
  size?: number;
};


/** 单批次上限 100（对齐 S5-03 MAX_BATCH_SIZE，替代旧 ≤50 口径） */
export const QUICK_ACTION_MAX_BATCH_SIZE = 100;

type RiskRuleCategorySummary = {
  category: string;
  templateCount: number;
};

export type RiskRule = {
  ruleId: string;
  ruleCode: string;
  category: string;
  ruleType: string;
  platformParams: Record<string, unknown>;
  overrideParams: Record<string, unknown> | null;
  effectiveParams: Record<string, unknown>;
  overrideStatus: 'ENABLED' | 'DISABLED' | 'NONE';
  editable: boolean;
  hitAction: string;
  priority: number;
  effectiveStatus: string;
  version: number;
  overrideId: string | null;
};

export type RiskRuleListResult = {
  categories: RiskRuleCategorySummary[];
  rules: RiskRule[];
  total: number;
};

type RiskRuleOverrideResult = {
  ruleId: string;
  overrideId: string;
  version: number;
  effectiveStatus: string;
  effectiveParams: Record<string, unknown>;
};

// ================= mock 数据（默认/演示；VITE_USE_MOCK=false 走真实 API） =================

const nowIso = new Date().toISOString();

const MOCK_RADAR: RiskRadarMetric[] = [
  { metric: 'DECLINE_RATE', value: '3.20', level: 'GREEN', wow: '1.05', refreshedAt: nowIso },
  { metric: 'FRAUD_INTERCEPT_RATE', value: '0.40', level: 'GREEN', wow: '-0.10', refreshedAt: nowIso },
  { metric: 'THREEDS_CHALLENGE_RATE', value: '2.10', level: 'YELLOW', wow: '0.30', refreshedAt: nowIso },
  { metric: 'DISPUTE_RATE', value: '0.60', level: 'GREEN', wow: '0.05', refreshedAt: nowIso },
  { metric: 'APPROVE_RATE', value: '96.80', level: 'GREEN', wow: '0.90', refreshedAt: nowIso },
];

const MOCK_DRILLDOWN: Record<string, Record<string, unknown>[]> = {
  DECLINE_RATE: [
    { cardId: '41021', amount: '1200.00', currency: 'USD', declineCode: 'INSUFFICIENT_FUNDS', occurredAt: nowIso },
    { cardId: '41022', amount: '899.00', currency: 'USD', declineCode: 'RISK_DECLINE', occurredAt: nowIso },
  ],
  FRAUD_INTERCEPT_RATE: [
    { interceptId: 'INT-88001', transactionId: 'TX-88001', cardId: '41021', amount: '3200.00', currency: 'USD', riskScore: '92', matchedRules: ['VELOCITY_SINGLE_TXN'], occurredAt: nowIso, status: 'BLOCKED' },
  ],
  THREEDS_CHALLENGE_RATE: [],
  DISPUTE_RATE: [
    { disputeId: 'DSP-77001', transactionId: 'TX-77001', cardId: '41022', amount: '299.00', currency: 'USD', reason: '未收到货物', status: 'REVIEWING', createdAt: nowIso },
  ],
  APPROVE_RATE: [
    { cardId: '41020', amount: '58.00', currency: 'USD', occurredAt: nowIso },
    { cardId: '41019', amount: '120.00', currency: 'USD', occurredAt: nowIso },
  ],
};

const MOCK_USERS: RiskUser[] = [
  { userId: '41001', userName: 'Z**', score: 82, tags: ['FRAUD_SUSPECT', 'VELOCITY'], status: 'RESTRICT' },
  { userId: '41002', userName: 'L**', score: 45, tags: [], status: 'NORMAL' },
  { userId: '41003', userName: 'W**', score: 91, tags: ['FROZEN'], status: 'FROZEN' },
];

let mockActions: RiskQuickAction[] = [
  { actionId: 'AUD-9001', actionType: 'FREEZE', targetCount: 3, status: 'SUCCEED', operator: '商户 #10', createdAt: nowIso },
  { actionId: 'AUD-9000', actionType: 'LIMIT', targetCount: 1, status: 'SUCCEED', operator: '商户 #10', createdAt: nowIso },
];

const MOCK_RULES: RiskRule[] = [
  {
    ruleId: '900001', ruleCode: 'VELOCITY_SINGLE_TXN', category: 'VELOCITY', ruleType: 'THRESHOLD',
    platformParams: { field: 'amount', operator: '>=', value: 100000, window: 'PER_TX', currency: 'USD' }, overrideParams: null,
    effectiveParams: { field: 'amount', operator: '>=', value: 100000, window: 'PER_TX', currency: 'USD' },
    overrideStatus: 'NONE', editable: true, hitAction: 'REVIEW', priority: 5, effectiveStatus: 'LIVE', version: 1, overrideId: null,
  },
  {
    ruleId: '900002', ruleCode: 'VELOCITY_DAILY_TXN', category: 'VELOCITY', ruleType: 'THRESHOLD',
    platformParams: { field: 'amount', operator: '>=', value: 300000, window: 'DAILY', currency: 'USD' }, overrideParams: null,
    effectiveParams: { field: 'amount', operator: '>=', value: 300000, window: 'DAILY', currency: 'USD' },
    overrideStatus: 'NONE', editable: true, hitAction: 'REVIEW', priority: 4, effectiveStatus: 'LIVE', version: 1, overrideId: null,
  },
  {
    ruleId: '900003', ruleCode: 'VELOCITY_TXN_COUNT', category: 'VELOCITY', ruleType: 'THRESHOLD',
    platformParams: { field: 'count', operator: '>=', value: 10, window: 'HOURLY' }, overrideParams: null,
    effectiveParams: { field: 'count', operator: '>=', value: 10, window: 'HOURLY' },
    overrideStatus: 'NONE', editable: true, hitAction: 'ALERT', priority: 3, effectiveStatus: 'LIVE', version: 1, overrideId: null,
  },
  {
    ruleId: '900004', ruleCode: 'AML_TXN_GT_10000', category: 'AML', ruleType: 'THRESHOLD',
    platformParams: { field: 'amount', operator: '>', value: 1000000, window: 'PER_TX', currency: 'USD' }, overrideParams: null,
    effectiveParams: { field: 'amount', operator: '>', value: 1000000, window: 'PER_TX', currency: 'USD' },
    overrideStatus: 'NONE', editable: false, hitAction: 'REVIEW', priority: 1, effectiveStatus: 'LIVE', version: 1, overrideId: null,
  },
  {
    ruleId: '900005', ruleCode: 'AML_STRUCTURING', category: 'AML', ruleType: 'THRESHOLD',
    platformParams: { field: 'count', operator: '>=', value: 5, window: 'HOURLY' }, overrideParams: null,
    effectiveParams: { field: 'count', operator: '>=', value: 5, window: 'HOURLY' },
    overrideStatus: 'NONE', editable: false, hitAction: 'REVIEW', priority: 1, effectiveStatus: 'LIVE', version: 1, overrideId: null,
  },
  {
    ruleId: '900006', ruleCode: 'OPEN_CARD_KYC_BELOW_L1', category: 'OPEN_CARD', ruleType: 'CUSTOM',
    platformParams: { handler: 'open_card_kyc_baseline', params: { minimumLevel: 'L1' } }, overrideParams: null,
    effectiveParams: { handler: 'open_card_kyc_baseline', params: { minimumLevel: 'L1' } },
    overrideStatus: 'NONE', editable: false, hitAction: 'BLOCK', priority: 1, effectiveStatus: 'LIVE', version: 1, overrideId: null,
  },
  {
    ruleId: '900007', ruleCode: 'WITHDRAW_VELOCITY', category: 'WITHDRAW', ruleType: 'THRESHOLD',
    platformParams: { field: 'count', operator: '>=', value: 3, window: 'DAILY' }, overrideParams: null,
    effectiveParams: { field: 'count', operator: '>=', value: 3, window: 'DAILY' },
    overrideStatus: 'NONE', editable: true, hitAction: 'REVIEW', priority: 5, effectiveStatus: 'LIVE', version: 1, overrideId: null,
  },
  {
    ruleId: '900008', ruleCode: 'DEPOSIT_VELOCITY', category: 'DEPOSIT', ruleType: 'THRESHOLD',
    platformParams: { field: 'count', operator: '>=', value: 5, window: 'HOURLY' }, overrideParams: null,
    effectiveParams: { field: 'count', operator: '>=', value: 5, window: 'HOURLY' },
    overrideStatus: 'NONE', editable: true, hitAction: 'ALERT', priority: 5, effectiveStatus: 'LIVE', version: 1, overrideId: null,
  },
  {
    ruleId: '900009', ruleCode: 'DEVICE_FINGERPRINT_CHANGE', category: 'DEVICE', ruleType: 'CUSTOM',
    platformParams: { handler: 'device_fingerprint_change', params: { windowDays: 7, changes: 2 } }, overrideParams: null,
    effectiveParams: { handler: 'device_fingerprint_change', params: { windowDays: 7, changes: 2 } },
    overrideStatus: 'NONE', editable: true, hitAction: 'REVIEW', priority: 4, effectiveStatus: 'LIVE', version: 1, overrideId: null,
  },
];

const MOCK_CATEGORIES: RiskRuleCategorySummary[] = [
  { category: 'VELOCITY', templateCount: 3 },
  { category: 'AML', templateCount: 2 },
  { category: 'OPEN_CARD', templateCount: 1 },
  { category: 'WITHDRAW', templateCount: 1 },
  { category: 'DEPOSIT', templateCount: 1 },
  { category: 'DEVICE', templateCount: 1 },
];

const CATEGORY_LABELS: Record<string, string> = {
  VELOCITY: '速率',
  AML: 'AML',
  OPEN_CARD: '开卡',
  WITHDRAW: '提现',
  DEPOSIT: '保证金',
  DEVICE: '设备指纹',
  GEO: '地域',
  MCC: 'MCC',
};

export function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

// ================= 风险雷达 =================

export async function fetchRadarMetrics(level?: string): Promise<ApiResult2<RiskRadarMetric[]>> {
  const query = level ? `?level=${encodeURIComponent(level)}` : '';
  return merchantGet<RiskRadarMetric[]>(
    `/risk/radar/metrics${query}`,
    () => (level && level !== 'ALL' ? MOCK_RADAR.filter((m) => m.level === level) : MOCK_RADAR),
  );
}

export async function fetchRadarDrilldown(metric: string): Promise<ApiResult2<RiskRadarDrilldown>> {
  return merchantGet<RiskRadarDrilldown>(
    `/risk/radar/metrics/${encodeURIComponent(metric)}/drilldown`,
    () => ({ metric, items: MOCK_DRILLDOWN[metric] ?? [] }),
  );
}

// ================= 用户风险监控 =================

export async function listRiskUsers(opts?: {
  status?: string;
  minScore?: number;
  page?: number;
  size?: number;
}): Promise<ApiResult2<RiskUserListResult>> {
  const q = new URLSearchParams();
  if (opts?.status && opts.status !== 'ALL') q.set('status', opts.status);
  if (opts?.minScore) q.set('minScore', String(opts.minScore));
  q.set('page', String(opts?.page ?? 1));
  q.set('size', String(opts?.size ?? 20));
  return merchantGet<RiskUserListResult>(`/risk/users?${q.toString()}`, () => {
    let items = MOCK_USERS;
    if (opts?.status && opts.status !== 'ALL') items = items.filter((u) => u.status === opts.status);
    if (opts?.minScore) items = items.filter((u) => u.score >= (opts.minScore ?? 0));
    return { items, total: items.length, page: opts?.page ?? 1, size: opts?.size ?? 20 };
  });
}

export async function changeRiskUserStatus(
  userId: string,
  targetStatus: string,
  reason?: string,
): Promise<ApiResult2<RiskUser>> {
  return merchantPost<RiskUser>(
    `/risk/users/${userId}/status`,
    { targetStatus, reason },
    () => {
      const u = MOCK_USERS.find((x) => x.userId === userId);
      if (!u) throw new Error('用户不存在');
      const updated = { ...u, status: targetStatus };
      MOCK_USERS.splice(MOCK_USERS.indexOf(u), 1, updated);
      return updated;
    },
  );
}

// ================= 快捷处置 =================

export async function listQuickActions(actionType?: string): Promise<ApiResult2<RiskQuickActionListResult>> {
  const query = actionType && actionType !== 'ALL'
    ? `?actionType=${encodeURIComponent(actionType)}`
    : '';
  return merchantGet<RiskQuickActionListResult>(
    `/risk/quick-actions${query}`,
    () => {
      const items = actionType && actionType !== 'ALL' ? mockActions.filter((a) => a.actionType === actionType) : mockActions;
      return { items, total: items.length };
    },
  );
}

export async function batchFreezeCards(
  cardIds: number[],
  reason?: string,
): Promise<ApiResult2<RiskQuickActionSubmitResult>> {
  return merchantPost<RiskQuickActionSubmitResult>(
    '/risk/quick-actions/batch-freeze',
    { cardIds, reason },
    () => {
      const r: RiskQuickActionSubmitResult = {
        actionId: `AUD-${Date.now()}`, actionType: 'FREEZE', targetCount: cardIds.length,
        succeededCount: cardIds.length, status: 'SUCCEED', createdAt: new Date().toISOString(),
      };
      mockActions = [{ actionId: r.actionId, actionType: 'FREEZE', targetCount: r.targetCount, status: 'SUCCEED', operator: '商户 #10', createdAt: r.createdAt }, ...mockActions];
      return r;
    },
  );
}

// ===== 批量处置改版（S5-05，spec §3.2 冻结契约） =====

/**
 * 候选卡筛选查询（GET /risk/quick-actions/cards，spec §3.2 新增）。
 * cardholderName/mobile/cardNo/cardId/shortCode 任一或多条件模糊；仅本商户名下卡。
 */
export async function fetchQuickActionCards(
  filter: QuickActionCardFilter,
): Promise<ApiResult2<QuickActionCardPage>> {
  const q = new URLSearchParams();
  if (filter.cardholderName?.trim()) q.set('cardholderName', filter.cardholderName.trim());
  if (filter.mobile?.trim()) q.set('mobile', filter.mobile.trim());
  if (filter.cardNo?.trim()) q.set('cardNo', filter.cardNo.trim());
  if (filter.cardId?.trim()) q.set('cardId', filter.cardId.trim());
  if (filter.shortCode?.trim()) q.set('shortCode', filter.shortCode.trim());
  q.set('page', String(filter.page ?? 1));
  q.set('size', String(filter.size ?? 50));
  return merchantGet<QuickActionCardPage>(`/risk/quick-actions/cards?${q.toString()}`, () => {
    throw new Error('USE_MOCK=false 时不应调用 mock 回调');
  });
}

/**
 * 批量解冻（POST /quick-actions/batch-unfreeze，幂等；非 FROZEN 跳过进明细）。
 * 资金外写操作同样注入 Idempotency-Key（后端 required=false，前端统一带上）。
 */
export async function batchUnfreezeCards(
  cardIds: number[],
  reason?: string,
): Promise<ApiResult2<QuickActionBatchUnfreezeResult>> {
  return merchantPost<QuickActionBatchUnfreezeResult>(
    '/risk/quick-actions/batch-unfreeze',
    { cardIds, reason },
    () => {
      throw new Error('USE_MOCK=false 时不应调用 mock 回调');
    },
  );
}

export async function batchAdjustLimits(
  cardIds: number[],
  limits: { limitSingle?: number; limitDaily?: number; limitMonthly?: number },
  reason?: string,
): Promise<ApiResult2<RiskQuickActionSubmitResult>> {
  return merchantPost<RiskQuickActionSubmitResult>(
    '/risk/quick-actions/batch-limit',
    { cardIds, ...limits, reason },
    () => {
      const r: RiskQuickActionSubmitResult = {
        actionId: `AUD-${Date.now()}`, actionType: 'LIMIT', targetCount: cardIds.length,
        succeededCount: cardIds.length, status: 'SUCCEED', createdAt: new Date().toISOString(),
      };
      mockActions = [{ actionId: r.actionId, actionType: 'LIMIT', targetCount: r.targetCount, status: 'SUCCEED', operator: '商户 #10', createdAt: r.createdAt }, ...mockActions];
      return r;
    },
  );
}

export async function submitDispute(body: {
  cardId: number;
  transactionId?: string;
  amount?: number;
  currency?: string;
  reasonCode: string;
  reason: string;
}): Promise<ApiResult2<RiskQuickActionSubmitResult>> {
  return merchantPost<RiskQuickActionSubmitResult>(
    '/risk/quick-actions/disputes',
    body,
    () => {
      const r: RiskQuickActionSubmitResult = {
        actionId: `DSP-${Date.now()}`, actionType: 'DISPUTE', targetCount: 1,
        succeededCount: 1, status: 'SUCCEED', createdAt: new Date().toISOString(),
      };
      mockActions = [{ actionId: r.actionId, actionType: 'DISPUTE', targetCount: 1, status: 'SUCCEED', operator: '商户 #10', createdAt: r.createdAt }, ...mockActions];
      return r;
    },
  );
}

// ================= 规则覆盖配置 =================

export async function listRiskRules(opts?: {
  category?: string;
  overrideStatus?: string;
  editable?: string;
}): Promise<ApiResult2<RiskRuleListResult>> {
  const q = new URLSearchParams();
  if (opts?.category && opts.category !== 'ALL') q.set('category', opts.category);
  if (opts?.overrideStatus && opts.overrideStatus !== 'ALL') q.set('overrideStatus', opts.overrideStatus);
  if (opts?.editable && opts.editable !== 'ALL') q.set('editable', opts.editable);
  return merchantGet<RiskRuleListResult>(`/risk/rules?${q.toString()}`, () => {
    let rules = MOCK_RULES;
    if (opts?.category && opts.category !== 'ALL') rules = rules.filter((r) => r.category === opts.category);
    if (opts?.overrideStatus && opts.overrideStatus !== 'ALL') rules = rules.filter((r) => r.overrideStatus === opts.overrideStatus);
    if (opts?.editable && opts.editable !== 'ALL') rules = rules.filter((r) => String(r.editable) === opts.editable);
    return { categories: MOCK_CATEGORIES, rules, total: rules.length };
  });
}

export async function putRuleOverride(
  ruleId: string,
  body: { paramsOverride?: Record<string, unknown>; status: 'LIVE' | 'DISABLED' },
  ifMatch?: number,
): Promise<ApiResult2<RiskRuleOverrideResult>> {
  return merchantPut<RiskRuleOverrideResult>(
    `/risk/rules/${ruleId}/override`,
    body,
    () => {
      const rule = MOCK_RULES.find((item) => item.ruleId === ruleId);
      if (!rule) throw new Error('规则不存在');
      const isNew = rule.overrideId == null;
      const delta = body.paramsOverride === undefined
        ? (rule.overrideParams ?? {}) : body.paramsOverride;
      const effectiveParams = mergeParams(rule.platformParams, delta);
      rule.overrideId ??= `9${Date.now()}`;
      rule.overrideParams = delta;
      rule.effectiveParams = effectiveParams;
      rule.overrideStatus = body.status === 'DISABLED' ? 'DISABLED' : 'ENABLED';
      rule.effectiveStatus = 'LIVE';
      rule.version = isNew ? 1 : (ifMatch ?? rule.version) + 1;
      return {
        ruleId,
        overrideId: rule.overrideId,
        version: rule.version,
        effectiveStatus: rule.effectiveStatus,
        effectiveParams,
      };
    },
    { headers: ifMatch !== undefined ? { 'If-Match': String(ifMatch) } : {} },
  );
}

function mergeParams(base: Record<string, unknown>, delta: Record<string, unknown>): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...base };
  Object.entries(delta).forEach(([key, value]) => {
    const previous = merged[key];
    if (previous && value && typeof previous === 'object' && typeof value === 'object'
      && !Array.isArray(previous) && !Array.isArray(value)) {
      merged[key] = mergeParams(previous as Record<string, unknown>, value as Record<string, unknown>);
    } else {
      merged[key] = value;
    }
  });
  return merged;
}

// 失败分支补可选 code 对齐 client.ApiResult（live 透传 merchantGet 结果本就携带 code，
// 此前仅类型收窄丢失；mapApiError 接入需读取该字段，mock 分支不受影响）
type ApiResult2<T> = { ok: true; data: T } | { ok: false; code?: string; error: string };
